import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { AvatarPreview } from "../components/AvatarEditor";
import { isLessonUnlocked } from "../local/profile";
import { SEED_LESSONS } from "../data/seed-lessons";
import { listCustomLessons } from "../local/customLessons";
import { rewardSettingsForLesson } from "../game/types";

/** Lesson order index → category color class (from design.css). */
const CATEGORY_BY_INDEX = [
  "cat-family",
  "cat-classroom",
  "cat-places",
  "cat-food",
  "cat-animals",
  "cat-feelings",
  "cat-numbers",
  "cat-actions",
  "cat-actions",
  "cat-builders",
] as const;

/**
 * Level select. Lessons 1-3 unlocked by default. Lesson N (N >= 4) requires
 * finishing lesson N-1 (any successful run that crosses the finish line).
 * Locked tiles render dimmed with a 🔒 overlay.
 */
export function LocalPlay() {
  const { profile } = useLocalProfile();
  const nav = useNavigate();
  const lessons = useMemo(
    () => {
      // Seed lessons first (ordered), then any teacher-built custom lessons
      // (most recently updated first). Custom lessons keep their isCustom
      // flag so the tile can show a "by you" badge.
      const seed = [...SEED_LESSONS].sort((a, b) => a.orderIndex - b.orderIndex);
      const custom = listCustomLessons();
      return [...seed, ...custom];
    },
    [],
  );

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }

  const level = 1 + Math.floor(profile.coins / 200);
  const xpInLevel = profile.coins % 200;

  return (
    <div className="home-dashboard level-select-page">
      <div className="home-topbar">
        <button className="home-player-pill" onClick={() => nav("/")}>
          <span className="home-player-face">
            <AvatarPreview cfg={profile.avatarConfig} />
          </span>
          <span>
            <span className="home-player-name">{profile.displayName}</span>
            <span className="home-player-level">Level {level}</span>
          </span>
        </button>
        <div className="home-resource-row">
          <div className="home-resource-pill">
            <span className="home-resource-icon">●</span>
            <strong>{profile.coins}</strong>
          </div>
          <button className="home-settings-btn" onClick={() => nav("/")} aria-label="Menu">
            ←
          </button>
        </div>
      </div>

      <section className="level-select-hero">
        <div>
          <p>Choose a Run</p>
          <h1>Levels</h1>
        </div>
        <div className="level-select-xp">
          <span>XP</span>
          <i><b style={{ width: `${(xpInLevel / 200) * 100}%` }} /></i>
        </div>
      </section>

      <div className="level-grid">
        {lessons.map((l, idx) => {
          const totalWords = l.words.length;
          const learned = l.words.filter(
            (w) => (profile.mastery[w.id]?.timesCorrect ?? 0) >= 2,
          ).length;
          const pct = totalWords ? (learned / totalWords) * 100 : 0;
          const cat = CATEGORY_BY_INDEX[idx % CATEGORY_BY_INDEX.length] ?? "cat-classroom";
          const heroImage = l.words.find((w) => w.imageUrl)?.imageUrl ?? null;
          const titleClean = l.title.replace(/^Level \d+:\s*/, "");
          const unlocked = isLessonUnlocked(profile, l.orderIndex, lessons);
          const rewards = rewardSettingsForLesson(l.orderIndex, l.coinsCount);
          const prevLesson = lessons.find((p) => p.orderIndex === l.orderIndex - 1);
          const prevName = prevLesson?.title.replace(/^Level \d+:\s*/, "");

          if (!unlocked) {
            return (
              <div key={l.id} className={`home-level-card ${cat} lesson-locked`}>
                <div className="home-level-num">
                  {l.orderIndex}
                </div>
                <div className="home-level-title">{titleClean}</div>
                <div className="level-reward-pill">● up to {rewards.coinsCount * rewards.coinPickupValue}</div>
                <div className="home-level-art">
                  {heroImage && <img src={heroImage} alt="" loading="lazy" />}
                </div>
                <div className="lesson-locked-overlay">
                  <div className="lesson-locked-icon">🔒</div>
                  <div className="lesson-locked-msg">
                    Finish <strong>{prevName ?? `Level ${l.orderIndex - 1}`}</strong> to unlock
                  </div>
                </div>
              </div>
            );
          }
          return (
            <Link
              key={l.id}
              to={`/play/lesson/${l.id}`}
              className={`home-level-card ${cat}`}
            >
              <div className="home-level-num">{l.orderIndex}</div>
              <div className="home-level-title">{titleClean}</div>
              <div className="level-reward-pill">● up to {rewards.coinsCount * rewards.coinPickupValue}</div>
              <div className="home-level-art">
                {heroImage && <img src={heroImage} alt="" loading="lazy" />}
              </div>
              <div className="home-level-progress">
                <span>★ {learned}/{totalWords}</span>
                <i><b style={{ width: `${pct}%` }} /></i>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
